import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileService } from '@/services/profile.service';
import { useAuthStore } from '@/stores/auth.store';
import type { MeProfile, UpdateMyProfilePayload } from '@/types/profile.types';

export const MY_PROFILE_KEY = ['auth', 'me-profile'] as const;

/** Mirrors the display-safe part of a fresh profile into the persisted auth user. */
function syncStore(me: MeProfile): void {
  useAuthStore.getState().updateUser({
    firstName: me.firstName,
    lastName: me.lastName,
    email: me.email,
    username: me.username,
    isSharedEmailAccount: me.isSharedEmailAccount,
    profilePhotoUrl: me.profilePhotoUrl,
    roleName: me.roleName,
    roleSlug: me.roleSlug,
  });
}

/**
 * User Profile P1 — the self-service profile query and mutations. Every call
 * is self-scoped (no id anywhere); the server decides what is editable and
 * returns `capabilities`, which the sections render from.
 */
export function useMyProfile() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: MY_PROFILE_KEY,
    queryFn: async () => {
      const me = await profileService.getMe();
      syncStore(me);
      return me;
    },
  });

  const settle = (me: MeProfile) => {
    qc.setQueryData(MY_PROFILE_KEY, me);
    syncStore(me);
  };

  const update = useMutation({
    mutationFn: (payload: UpdateMyProfilePayload) => profileService.updateMe(payload),
    onSuccess: settle,
  });
  const uploadPhoto = useMutation({
    mutationFn: (file: File) => profileService.uploadPhoto(file),
    onSuccess: settle,
  });
  const deletePhoto = useMutation({
    mutationFn: () => profileService.deletePhoto(),
    onSuccess: settle,
  });

  return { query, update, uploadPhoto, deletePhoto };
}
